/**
 * Open govt_opportunity counts per dashboard geographic zone for the Region toggle bar.
 * Single batched API call (not N× /dashboard-stats).
 */

import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { getAPIClient } from '@tracekit/core';
import {
  type GeographicZoneId,
  type GeographicZoneOpportunityCounts,
} from '../utils/countryAccess';

interface DashboardZoneCountsResponse {
  project_id: string;
  counts: Record<string, number>;
  cached_at: string | null;
}

export async function fetchGeographicZoneOpportunityCounts(
  projectId: string
): Promise<GeographicZoneOpportunityCounts> {
  const api = getAPIClient();
  const res = await api.get<DashboardZoneCountsResponse>(
    `/api/opportunities/dashboard-zone-counts?project_id=${encodeURIComponent(projectId)}`
  );
  const raw = res?.counts || {};
  return {
    world: Number(raw.world ?? 0),
    canada: Number(raw.canada ?? 0),
    usa: Number(raw.usa ?? 0),
    mexico: Number(raw.mexico ?? 0),
    africa: Number(raw.africa ?? 0),
    brazil: Number(raw.brazil ?? 0),
    india: Number(raw.india ?? 0),
  } as Record<GeographicZoneId, number>;
}

export function useGeographicZoneOpportunityCounts(
  projectId: string | null | undefined,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: ['govt-opportunities-zone-counts', projectId],
    queryFn: async () => {
      if (!projectId) throw new Error('Project ID required');
      return fetchGeographicZoneOpportunityCounts(projectId);
    },
    enabled: !!projectId && (options?.enabled ?? true),
    staleTime: 15 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: true,
  });
}
